import { EnumApiTokenCostRoute, IApiTokenCostConfigEntry } from '@libs/constants';
import { Validator, ValidatorSchema } from '@libs/utilities/validator';
import { FLAI_REQUEST_CONFIG } from '@domains/application-knowledge/shared';

import { IAgentWorkflowCommand } from './agent-workflow.interface';


export const FLYING_AGENT_EXPLAIN_ROUTE: string = '/agent/explain-function';

export const FLYING_AGENT_API_TOKEN_CONFIG: Record<EnumApiTokenCostRoute.AgentExplain, IApiTokenCostConfigEntry> = {
	[EnumApiTokenCostRoute.AgentExplain]: {
		name			: 'FLAI Explain API',
		description		: 'Explains the current Flying page and available actions',
		reasonText		: 'FLAI explanation',
		apiTokenCosts	: { request: 1 },
		costCalculateFunction: (): number => (FLAI_REQUEST_CONFIG.deductCredits ? 1 : 0)
	}
};


/**------------------------------------------------------
 * Requests
 */
export interface IFlyingAgentExplainRequest {
	workflow?: boolean;
	question	: string;
	route		: string;
	pageTitle	: string;
	selection?	: string;
	selectedItem?	: IFlyingAgentSelectedItem;
	availableActions?: EnumFlyingAgentPageAction[];
	history?: IFlyingAgentChatMessage[];
	pageData?: string;
}

export interface IFlyingAgentChatMessage {
	role: 'user' | 'assistant';
	text: string;
	pageTitle?: string;
}

export interface IFlyingAgentSelectedItem {
	type	: string;
	label	: string;
	id?		: string;
}

export enum EnumFlyingAgentPageAction {
	FocusSearch	= 'focus-search',
	ClearSearch	= 'clear-search',
	RunRealTimeResearch = 'run-real-time-research',
	RunBestSellers = 'run-best-sellers',
	RunSellerTrends = 'run-seller-trends'
}


export type TypeFlyingAgentResearchAction = EnumFlyingAgentPageAction.RunRealTimeResearch | EnumFlyingAgentPageAction.RunBestSellers | EnumFlyingAgentPageAction.RunSellerTrends;

export const FLYING_AGENT_RESEARCH_PAGES: Record<TypeFlyingAgentResearchAction, { route: string; title: string; functionName: string }> = {
	[EnumFlyingAgentPageAction.RunRealTimeResearch]: { route: '/research/research/real-time-research', title: 'Real Time Research', functionName: 'run_real_time_research' },
	[EnumFlyingAgentPageAction.RunBestSellers]: { route: '/research/research/best-sellers', title: 'Best Sellers', functionName: 'run_best_sellers' },
	[EnumFlyingAgentPageAction.RunSellerTrends]: { route: '/research/research/seller-trends', title: 'Seller Trends', functionName: 'run_seller_trends' }
};

export function getFlyingAgentResearchAction(route: string, availableActions: EnumFlyingAgentPageAction[]): TypeFlyingAgentResearchAction | undefined {
	return (Object.keys(FLYING_AGENT_RESEARCH_PAGES) as TypeFlyingAgentResearchAction[]).find((code) => FLYING_AGENT_RESEARCH_PAGES[code].route === route.split(/[?#]/)[0].replace(/\/+$/, '') && availableActions.includes(code));
}


export interface IFlyingAgentResearchAction {
	code: TypeFlyingAgentResearchAction;
	searchTerm: string;
}


/**------------------------------------------------------
 * Streaming Responses
 */
export enum EnumFlyingAgentExplainStreamEventType {
	Action = 'action',
	Delta		= 'delta',
	Reset		= 'reset',
	Completed	= 'completed'
}

export interface IFlyingAgentExplainStreamEvent {
	command?: IAgentWorkflowCommand;
	stepId?: string;
	type		: EnumFlyingAgentExplainStreamEventType;
	delta?		: string;
	markdown?	: string;
	tokensUsed?	: number;
	creditsUsed?	: number;
	creditBalanceAfter?: number;
	action?: IFlyingAgentResearchAction;
}


/**------------------------------------------------------
 * Schemas
 */
export class FlyingAgentExplainRequestSchema implements ValidatorSchema<IFlyingAgentExplainRequest> {
	workflow = Validator.Boolean().optional();
	question	= Validator.String().isNotEmpty().isMaxLength(300).hasNoHtmlScripts().trim().purifyWhitespaces();
	route		= Validator.String().isNotEmpty().isMaxLength(500).hasNoHtmlScripts().trim();
	pageTitle	= Validator.String().isNotEmpty().isMaxLength(160).hasNoHtmlScripts().trim().purifyWhitespaces();
	selection	= Validator.String().isMaxLength(1000).hasNoHtmlScripts().trim().purifyWhitespaces().optional();
	selectedItem = Validator.Object().optional().inspect<IFlyingAgentSelectedItem>({
		type	: Validator.String().isNotEmpty().isMaxLength(80).hasNoHtmlScripts().trim().purifyWhitespaces(),
		label	: Validator.String().isNotEmpty().isMaxLength(240).hasNoHtmlScripts().trim().purifyWhitespaces(),
		id		: Validator.String().isMaxLength(160).hasNoHtmlScripts().trim().purifyWhitespaces().optional()
	});
	history = Validator.Array().isMaxSize(10).each(Validator.Object().inspect<IFlyingAgentChatMessage>({
		role: Validator.Enum({ User: 'user', Assistant: 'assistant' }),
		text: Validator.String().isNotEmpty().isMaxLength(2000),
		pageTitle: Validator.String().isMaxLength(160).optional()
	})).optional();
	pageData = Validator.String().isMaxLength(20000).optional();
	availableActions = Validator.Array().each(Validator.Enum(EnumFlyingAgentPageAction)).isMaxSize(4).optional();
}

export class FlyingAgentExplainStreamEventSchema implements ValidatorSchema<IFlyingAgentExplainStreamEvent> {
	command = Validator.Object().optional();
	stepId = Validator.String().isMaxLength(80).optional();
	type		= Validator.Enum(EnumFlyingAgentExplainStreamEventType).isNotEmpty();
	delta		= Validator.String().isMaxLength(12000).optional();
	markdown	= Validator.String().isNotEmpty().isMaxLength(12000).optional();
	tokensUsed	= Validator.Number().isGreaterThanOrEqual(0).optional();
	creditsUsed	= Validator.Number().isGreaterThanOrEqual(0).optional();
	action = Validator.Object().optional().inspect<IFlyingAgentResearchAction>({
		code: Validator.Enum(EnumFlyingAgentPageAction).isNotEmpty(),
		searchTerm: Validator.String().isNotEmpty().isMaxLength(500).trim()
	});
	creditBalanceAfter = Validator.Number().isGreaterThanOrEqual(0).optional();
}
